import React, { useEffect, useRef, useState } from 'react';
import ProjectsService from '../../JourneyProfiles/List/JourneyProfileService';
import ProjectsForm from './ProjectsForm';
import FormTemplateService from '../../../services/FormTemplateService';
import useModal from '../../../hooks/useModal';
import GenericModal from '../../Modals/GenericModal';
import CommentsModal from '../../Modals/CommentsModal';
import ConfirmMenu from '../../Modals/ConfirmMenu';
import { Spinner } from '../../Common/Spinner';
import { useCurrentUserHook } from '../../Authentication/useUserHook';
import DynamicTable from '../../Common/DynamicTable';
import { DraftBadge } from '../../Common/NotificationPill';
import { LinkFontAwesomeIcon, LinkDeleteIcon } from '../../Common/ImageIcons';
import PaginationSM from '../../Common/PaginationSM';
import MatchingSuppliers from './MatchingSuppliers';
import CfpResponsesModal from './CfpResponsesModal';
  
const FORM_ID = 409;

const ProjectsTab = ({orgRegistration}) => {
  let { current: scope } = useRef({ requestParams: {} });
  const { currentUser } = useCurrentUserHook();
  const [projects, setProjects] = useState([]);
  const [pagemeta, setPagemeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [tableFields, setTableFields] = useState();

  const { isOpen: isProjectFormOpen, toggleModal: toggleProjectFormModal } = useModal();
  const { isOpen: isCommentOpen, toggleModal: toggleCommentModal } = useModal();
  const { isOpen: isDeleteOpen, toggleModal: toggleDeleteModal } = useModal();
  // const { isOpen: isMatchingSupplierOpen, toggleModal: toggleMatchingSupplier } = useModal();
  const { isOpen: isResponsesOpen, toggleModal: toggleResponsesModal } = useModal();


  useEffect(() => {
    setParams(1);
    getAll();
  }, []);

  useEffect(() => {
    getFormFields();
  }, []);

  const setParams = (page) => {
    scope.requestParams = {
      page,
      paginate: 1,
      per_page: 15,
      organization_id: currentUser.current_organization_id,
      journey_type: 'project',
      form_id: FORM_ID,
      sort_column: scope.requestParams.sort_column || 'updated_at',
      sort_direction: scope.requestParams.sort_direction || 'desc',
    };
  };

  const onPageChange = (page) => {
    setParams(page);
    getAll();
  };

  const getFormFields = async () => {
    setLoading(true);
    const {status, data} = await FormTemplateService.getFormFields({ ids: [FORM_ID], is_table_view: true });
    if (status === 200) {
      setTableFields(data.form_fields?.[FORM_ID]);
    } else {
      setTableFields([]);
    }
    setLoading(false);
  };

  const getAll = async () => {
    setLoading(true);
    const { status, data } = await ProjectsService.getAll(scope.requestParams);
    if (status === 200) {
      setProjects(data.journey_profiles ?? []);
      setPagemeta(data.meta ?? {});
    } else {
      setProjects([]);
      setPagemeta({});
    }
    setLoading(false);
  };

  const onSubmit = () => {
    setParams(1);
    getAll();
  };

  const openProjectFormModal = (project) => {
    scope.projectFormProps = {
      editProject: project,
      isPopupMode: true,
      onSubmit: onSubmit,
    };
    toggleProjectFormModal();
  };

  const addNotes = (project) => {
    scope.selectedProject = project;
    toggleCommentModal();
  };

  const onDelete = (project) => {
    scope.selectedProject = project;
    toggleDeleteModal();
  };

  const deleteProject = async (project) => {
    setLoading(true);
    const { status } = await ProjectsService.delete(project.id);
    if (status === 204) {
      setProjects(projects.filter((i) => i.id !== project.id));
    }
    setLoading(false);
    toggleDeleteModal();
  };

  // const openSuppliers = (project) => {
  //   scope.selectedProject = project;
  //   toggleMatchingSupplier();
  // };

  const openResponses = (submission) => {
    scope.selectedSubmission = submission;
    toggleResponsesModal();
  };

  const recordId = (clientId, data, row) => (
    <span onClick={() => openProjectFormModal(row)} className="pointer">
      <div className="m-l-10">{row.record_id}</div>
      <DraftBadge isDraft={row.draft} styleName="d-block brd-2 w-50 fw500 color-black bg-white br-red" />
    </span>
  );

  const endDateEle = (clientId, data, row) => (
    <span className="flex aligncenter gap-5 font-12">
      {data?.end_date || '-'}
    </span>
  );


  const responsesEle = () => (
    <div className="flex coldir gap-3">
      <span className="badge badge-success badge-xxs font-11 fw500 w-100p text-center">1 total</span>
      <span className="badge badge-xxs font-11 fw500 w-100p bg-blue-white text-blue flex aligncenter justifycenter gap-5">
        <span className="circle-xs bg-lightblue"></span>
        1 new
      </span>
    </div>
  );

  const specialFields = [
    { label: 'ID', client_id: 'record_id', ele: recordId },
    { label: 'End Date', client_id: 'end_date', ele: endDateEle },
    { label: 'Responses', client_id: 'responses', ele: responsesEle },
  ];

  const OptionsEle = ({ item }) => (
    <td>
      <div className="flex aligncenter gap-10">
        <LinkFontAwesomeIcon tooltip="Edit" onSelect={() => openProjectFormModal(item)} iconCss="fas fa-pen white" />
        <LinkFontAwesomeIcon tooltip="Add Comments" iconCss="fas fa-comments" onSelect={() => addNotes(item)} />
        <LinkDeleteIcon id="delete" tooltip="Delete Project" onSelect={() => onDelete(item)} />
        <LinkFontAwesomeIcon id="suppliers" tooltip="Find Suppliers" onSelect={() => openResponses(item)} iconCss="fas fa-users white"/>
      </div>
    </td>
  );

  return (
    <>
      <div className="flex justspacebetween aligncenter m-b-15">
        <span className="fw700 font-18 dark-navy">Projects</span>
        <button
          className="btn-primary bg-green1-dark text-white brd-5 font-13 fw500 p-8-15 flex aligncenter gap-5"
          onClick={() => openProjectFormModal()}>
          <i className="fas fa-plus font-12"></i> New Project
        </button>
      </div>

      {loading && tableFields?.length > 0 ? <Spinner /> : (
        <div className="app-content-section m-t-15 table-fix-head">
          <DynamicTable
            requestParams={{}}
            formFields={tableFields}
            formDataList={projects}
            tClass="table shadow-small"
            OptionsEle={OptionsEle}
            specialFields={specialFields} />
          <PaginationSM
            className="pagination-bar"
            currentPage={parseInt(pagemeta.current_page)}
            totalItems={pagemeta.total}
            itemsPerPage={pagemeta.per_page}
            onPageChange={onPageChange}
            siblingCount={2} />
        </div>
      )}

      {isProjectFormOpen && (
        <GenericModal component={ProjectsForm} isOpen={isProjectFormOpen} toggleModal={toggleProjectFormModal} {...scope.projectFormProps} />
      )}

      {isCommentOpen && (
        <CommentsModal isOpen={isCommentOpen} toggleModal={toggleCommentModal} item={scope.selectedProject} type="project" />
      )}
       
      {/* {isMatchingSupplierOpen && (
        <GenericModal 
          component={MatchingSuppliers} 
          isOpen={isMatchingSupplierOpen} 
          toggleModal={toggleMatchingSupplier} 
          orgRegistration={orgRegistration}
          projectRequest={scope.selectedProject}/>
      )}  */}

      {isResponsesOpen && (
        <GenericModal
          component={CfpResponsesModal}
          isOpen={isResponsesOpen}
          toggleModal={toggleResponsesModal}
          submission={scope.selectedSubmission} />
      )}

      <ConfirmMenu isOpen={isDeleteOpen} toggleModal={toggleDeleteModal} success={() => deleteProject(scope.selectedProject)} />
    </>
  );
};

export default ProjectsTab;